import classnames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import TextareaAutosize from "react-textarea-autosize";
import { AppEventPublisher, StoryBibleAction } from "../../../AppEventPublisher";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import { useAppStore } from "../../../stores/appStore";
import { useProjectStore } from "../../../stores/projectStore";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import { sendInvocationEvent } from "../../../Utils";
import Thumbs from "../../TiptapDocumentSB/Thumbs";
import Header from "../Header";
import SendToDoc from "../SendToDoc";
import Textarea from "../Textarea";
import { OutlineTemplateLink } from "./OutlineTemplateLink";
import { CopyableTextAreaContainer } from "./styles";
import FieldLoading from "../../CustomField/FieldLoading/index.js";
import { BLANK_TIPTAP_JSON } from "../../../../../src/shared/schemas/CharacterSchema";

function CopyableTextarea({
  documentScenes,
  // requesting,
  generate,
  generateLabel,
  generateSceneDirections,
  pausable,
  paused,
  onPause,
  requestPause,
  pauseRequested,
  onContinue,
  continueLabel,
  help,
  value,
  history,
  setValue,
  className,
  containerClassName,
  width,
  title,
  titleAccessory,
  titleClassName,
  maxLength,
  onInput,
  onBlur,
  //openHelp,
  //closeHelp,
  // abortStream,
  // recentInvocations,
  // setRecentInvocations,
  type,
  id,
  // outOfQuota,
  loadAnimationDuration,
  // isStreaming,
  placeholder,
  sceneId,
  setScenes,
  sceneKey,
  height,
  rewritePlaceholder,
  feedbackValue,
  setFeedbackValue,
  onRewriteWithFeedback,
  isInChapter,
  proseModes,
  proseMode,
  userData,
  sendToDocument = false,
  sendToDocumentTitle,
  generateButtonType,
  generateButtonMode,
  generateButtonOpenModal,
  generateDisabled,
  generateDisabledMessage,
  bottomBar,
  bottomBarRef,
  topComponents,
  iconPath,
  ariaLabel,
  preHeaderComponents,
  headerRightComponents,
  startFocused = false,
  showDotsMenu,
  actions,
  dotsMenuAnchorId,
  syncing = false,
  collapsed = false,
  onToggleCollapse,
  headerAnchorId,
  headingId,
  fieldKey, // For meta field diff highlighting: "synopsis", "genre", "style", "seed"
  onFocusCallback: onFocusCallbackProp,
  ctaShimmer,
  ctaEnabled,
}) {
  const recentInvocations = useStoryBibleStore((state) => state.recentInvocations);
  const requesting = useStoryBibleStore((state) => state.requesting);
  const abortStream = useStoryBibleStore((state) => state.abortStream);
  const outOfQuota = useStoryBibleStore((state) => state.outOfQuota);
  const isStreaming = useStoryBibleStore((state) => state.isStreaming);
  const lastInvocation = useStoryBibleStore((state) => state.lastInvocation);
  const setLastInvocation = useStoryBibleStore((state) => state.setLastInvocation);
  const clearLastInvocation = useStoryBibleStore((state) => state.clearLastInvocation);
  const setFeedbackOpen = useProjectStore((state) => state.setFeedbackOpen);
  const setFeedbackInvocation = useProjectStore((state) => state.setFeedbackInvocation);
  const project = useProjectStore((state) => state.project);
  const metaDiffs = useStoryBibleStore((state) => state.metaDiffs);
  const clearMetaDiff = useStoryBibleStore((state) => state.clearMetaDiff);
  const isMac = useAppStore((state) => state.isMac);
  const hasMetaDiff = fieldKey && metaDiffs[fieldKey];
  const [showHoverIcons, setShowHoverIcons] = useState(false);
  const [showThumbs, setShowThumbs] = useState(false);
  const [guidanceVisible, setGuidanceVisible] = useState(false);
  const guidanceRef = useRef(null);
  const guidanceInputRef = useRef(null);
  const guidanceContainerRef = useRef(null);
  const [textAreaFocused, setTextAreaFocused] = useState(false);

  useOnClickOutside(guidanceRef, () => {
    setGuidanceVisible(false);
  });

  useEffect(() => {
    if (guidanceVisible && guidanceInputRef.current) {
      guidanceInputRef.current.focus();
    }
  }, [guidanceVisible]);

  useEffect(() => {
    setLastInvocation(recentInvocations.lastInvocationId);
  }, [recentInvocations, id]);

  const onCopy = useCallback(
    (e) => {
      sendInvocationEvent(lastInvocation, "select-copy", e.target.value);
      AppEventPublisher.storyBible.copy(type, "select");
    },
    [lastInvocation]
  );

  const onChange = useCallback(
    (e) => {
      if (e.isTrusted) {
        if (!value && e.target.value) {
          AppEventPublisher.storyBible.edited(type, StoryBibleAction.EnteredTextFromBlank);
        }
        clearLastInvocation(type, id);
      }
    },
    [value, clearLastInvocation, type, id]
  );

  useEffect(() => {
    setShowThumbs(!isStreaming && lastInvocation && recentInvocations.lastInvocationType === type);
  }, [isStreaming, lastInvocation, recentInvocations.lastInvocationType, type]);

  const handleOpenGuidance = useCallback(() => {
    if (ctaEnabled && onRewriteWithFeedback) {
      onRewriteWithFeedback();
      return;
    }
    setGuidanceVisible(true);
    if (guidanceContainerRef.current) {
      guidanceContainerRef.current.focus({ preventScroll: true });
    }
  }, []);

  const handleCloseGuidance = useCallback(() => {
    setGuidanceVisible(false);
  }, []);

  const handleGuidanceSubmit = useCallback(
    (e) => {
      e.preventDefault();
      handleCloseGuidance();
      if (setLastInvocation) {
        setLastInvocation(null);
      }
      onRewriteWithFeedback && onRewriteWithFeedback();
    },
    [handleCloseGuidance, onRewriteWithFeedback, setLastInvocation]
  );

  const handleGuidanceKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        handleGuidanceSubmit(e);
      }
      if (e.key === "Escape") {
        handleCloseGuidance();
      }
    },
    [handleGuidanceSubmit]
  );

  return (
    <CopyableTextAreaContainer
      style={{ width: width + "px" }}
      className={classnames("copytable-textarea-component-container", containerClassName)}
      $textAreaFocused={textAreaFocused}
      onMouseEnter={() => setShowHoverIcons(true)}
      onMouseLeave={() => setShowHoverIcons(false)}
      onFocus={() => setShowHoverIcons(true)}
      onBlur={() => setShowHoverIcons(false)}
      tabIndex={0}
    >
      {preHeaderComponents}
      <Header
        generate={generate}
        generateLabel={generateLabel}
        className={`textarea-header ${type}-actions`}
        history={history}
        title={title}
        titleAccessory={titleAccessory}
        titleClassName={titleClassName}
        syncing={syncing}
        textContent={value}
        requesting={requesting}
        generateSceneDirections={generateSceneDirections}
        abortStream={abortStream}
        type={type}
        lastInvocation={lastInvocation}
        setFeedbackOpen={setFeedbackOpen}
        setFeedbackInvocation={setFeedbackInvocation}
        outOfQuota={outOfQuota}
        loadAnimationDuration={loadAnimationDuration}
        isStreaming={isStreaming}
        setLastInvocation={setLastInvocation}
        pausable={pausable}
        onPause={onPause}
        paused={paused}
        requestPause={requestPause}
        pauseRequested={pauseRequested}
        onContinue={onContinue}
        continueLabel={continueLabel}
        isInChapter={isInChapter}
        proseModes={proseModes}
        proseMode={proseMode}
        userData={userData}
        generateButtonType={generateButtonType}
        generateButtonMode={generateButtonMode}
        generateButtonOpenModal={generateButtonOpenModal}
        generateDisabled={generateDisabled}
        generateDisabledMessage={generateDisabledMessage}
        iconPath={iconPath}
        rightComponents={headerRightComponents}
        showHoverIcons={showHoverIcons}
        showDotsMenu={showDotsMenu}
        actions={actions}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        dotsMenuAnchorId={dotsMenuAnchorId}
        headerAnchorId={headerAnchorId}
        headingId={headingId}
        skipStoryBibleGenerateAnalyticsInvoke={!!ctaEnabled}
      />
      {!collapsed && (
        <>
          {topComponents}
          {ctaShimmer ? (
            <div className="textarea-container" style={{ padding: "0 10px 10px 10px" }}>
              <FieldLoading
                content={value ? { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: value }] }] } : BLANK_TIPTAP_JSON}
                loadingText={null}
                userId={null}
                projectId={null}
              />
            </div>
          ) : (
          <div className="textarea-container">
            <div
              className={`feedback-container ${sceneKey === "commands" && !project.useScenes ? "feedback-container-commands" : "feedback-container-scenes"}`}
              id={`textarea-container-${type}`}
            >
              <div
                className="relative rewrite-guidance-container"
                tabIndex={-1}
                ref={guidanceContainerRef}
              >
                <Textarea
                  id={`textarea-${type}`}
                  placeholder={placeholder}
                  onCopy={onCopy}
                  className={classnames("data-hj-allow", className, hasMetaDiff && "meta-field-diff")}
                  value={value}
                  setValue={setValue}
                  sceneId={sceneId}
                  setScenes={setScenes}
                  sceneKey={sceneKey}
                  onChange={onChange}
                  maxWords={maxLength}
                  help={help}
                  onInput={onInput}
                  onBlur={onBlur}
                  height={height}
                  aria-label={ariaLabel}
                  setShowThumbs={setShowThumbs}
                  offsetWordCount={showThumbs ? 38 : 0}
                  startFocused={startFocused}
                  generate={generate}
                  generateDisabled={generateDisabled}
                  generateDisabledMessage={generateDisabledMessage}
                  requesting={requesting}
                  outOfQuota={outOfQuota}
                  isStreaming={isStreaming}
                  setLastInvocation={setLastInvocation}
                  onRewriteWithFeedback={onRewriteWithFeedback}
                  feedbackValue={feedbackValue}
                  setFeedbackValue={setFeedbackValue}
                  rewritePlaceholder={rewritePlaceholder}
                  onOpenGuidance={handleOpenGuidance}
                  setTextAreaFocused={setTextAreaFocused}
                  abortStream={abortStream}
                  minRows={type === "style" && !value ? 5 : 1}
                  syncing={syncing}
                  skipStoryBibleGenerateAnalyticsInvoke={!!ctaEnabled}
                  // Enable global search for Story Bible fields
                  searchableId={`storybible-${type}`}
                  searchableName={title || type}
                  onFocusCallback={() => {
                    if (hasMetaDiff && fieldKey) clearMetaDiff(fieldKey);
                    onFocusCallbackProp?.();
                  }}
                />
                <AnimatePresence>
                  {!ctaEnabled && guidanceVisible && (
                    <motion.div
                      initial={{ opacity: 0, scaleY: 0.9, translateY: "100%" }}
                      animate={{ opacity: 1, scaleY: 1, translateY: "100%" }}
                      exit={{ opacity: 0, scaleY: 0.9, translateY: "100%" }}
                      transition={{ duration: 0.15 }}
                      ref={guidanceRef}
                      className="absolute left-2 bottom-[52px] z-[100] w-[518px] rounded-[14px] bg-[var(--menu-color)] p-[15px]"
                      style={{ boxShadow: "var(--card-drop-shadow)" }}
                    >
                      <form
                        className="flex items-end gap-2"
                        onSubmit={handleGuidanceSubmit}
                      >
                        <div className="flex grow flex-col gap-4">
                          <p className="text-[var(--grey-0)] font-medium">Custom Instructions (optional)</p>
                          <TextareaAutosize
                            value={feedbackValue}
                            onChange={(e) => setFeedbackValue(e.target.value)}
                            placeholder={rewritePlaceholder}
                            ref={guidanceInputRef}
                            onKeyDown={handleGuidanceKeyPress}
                            className="m-0 min-h-[24px] max-h-[100px] resize-none border-0 bg-transparent p-0 text-base text-[var(--grey-0)] outline-none focus:ring-0"
                          />
                        </div>
                        <div className="flex items-center">
                          <button
                            type="submit"
                            className="flex h-[37px] w-[105px] shrink-0 items-center gap-[10px] rounded-[10px] px-[12px] py-[8px]"
                            aria-label="Go"
                          >
                            Go
                            <div
                              className="flex items-center gap-[3px]"
                              aria-label={`Press ${isMac ? "command" : "control"} plus enter to generate a character with guidance`}
                              role="button"
                            >
                              <SVG
                                src={isMac ? "/assets/keyboard-shortcuts/command-key.svg" : "/assets/keyboard-shortcuts/control-key-pc.svg"}
                                className="shortcut-icon rounded-[5px] border-2 border-[rgba(255,255,255,0.4)] fill-[var(--cta-text-color)]"
                                aria-hidden="true"
                              />
                              <SVG
                                src="/assets/keyboard-shortcuts/enter-key.svg"
                                className="shortcut-icon rounded-[5px] border-2 border-[rgba(255,255,255,0.4)] fill-[var(--cta-text-color)]"
                                aria-hidden="true"
                              />
                            </div>
                          </button>
                        </div>
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {showThumbs && (
                <Thumbs
                  invocationId={lastInvocation}
                  key={lastInvocation}
                  setFeedbackOpen={setFeedbackOpen}
                  setFeedbackInvocation={setFeedbackInvocation}
                />
              )}
              {type === "outline" && !value && <OutlineTemplateLink setValue={setValue} />}
            </div>

            <div
              className="bottom-bar"
              ref={bottomBarRef}
            >
              {sendToDocument && project && (
                <SendToDoc
                  project={project}
                  content={value}
                  title={sendToDocumentTitle}
                />
              )}
              {bottomBar && bottomBar({ lastInvocation, setLastInvocation })}
            </div>
          </div>
          )}
        </>
      )}
    </CopyableTextAreaContainer>
  );
}

export default memo(CopyableTextarea);
